import { Transaction } from '@/hooks/useTransactions';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowDownLeft, ArrowUpRight, DollarSign, RefreshCw, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

interface TransactionListProps {
  transactions: Transaction[];
  loading?: boolean;
}

export const TransactionList = ({ transactions, loading }: TransactionListProps) => {
  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Card key={i} className="p-4 animate-pulse">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-muted rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted rounded w-1/3"></div>
                <div className="h-3 bg-muted rounded w-1/4"></div>
              </div>
              <div className="h-6 w-20 bg-muted rounded"></div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <Card className="p-8 text-center">
        <DollarSign className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
        <p className="text-muted-foreground">No transactions yet</p>
      </Card>
    );
  }

  const getTransactionIcon = (type: Transaction['type']) => {
    switch (type) {
      case 'deposit':
      case 'payment_received':
        return <ArrowDownLeft className="w-5 h-5 text-green-600" />;
      case 'withdrawal':
      case 'payment_sent':
      case 'fee':
        return <ArrowUpRight className="w-5 h-5 text-red-600" />;
      case 'refund':
        return <RefreshCw className="w-5 h-5 text-blue-600" />;
      default:
        return <DollarSign className="w-5 h-5" />;
    }
  };

  const getStatusBadge = (status: Transaction['status']) => {
    const variants: Record<Transaction['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
      completed: 'default',
      pending: 'secondary',
      failed: 'destructive',
      cancelled: 'outline',
    };

    return <Badge variant={variants[status]}>{status}</Badge>;
  };

  const isIncoming = (type: Transaction['type']) => {
    return ['deposit', 'payment_received', 'refund'].includes(type);
  };

  return (
    <div className="space-y-3">
      {transactions.map((transaction) => (
        <Card key={transaction.id} className="p-4 hover:bg-accent/50 transition-colors cursor-pointer">
          {/* Mobile (<sm): two-row layout — title+amount on row 1, metadata below.
              Long reference numbers (e.g. TASK-1776650941-028d9296-RECV) used to
              force the row to wrap into 4-line columns on a 400px viewport.
              Desktop (sm+): original 3-column flex (icon | content | amount). */}
          <div className="flex gap-3 sm:items-center sm:gap-4">
            <div className={`p-2 rounded-full flex-shrink-0 self-start sm:self-auto ${
              isIncoming(transaction.type) ? 'bg-green-500/10' : 'bg-red-500/10'
            }`}>
              {getTransactionIcon(transaction.type)}
            </div>

            <div className="flex-1 min-w-0">
              {/* Row 1 on mobile: title + amount aligned across the row */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <p className="font-medium truncate">
                    {transaction.description || transaction.type.replace(/_/g, ' ')}
                  </p>
                  <div className="hidden sm:block flex-shrink-0">
                    {getStatusBadge(transaction.status)}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 sm:hidden">
                  <p className={`text-base font-semibold whitespace-nowrap ${
                    isIncoming(transaction.type) ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {isIncoming(transaction.type) ? '+' : '-'}${Math.abs(transaction.amount).toFixed(2)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{transaction.currency}</p>
                </div>
              </div>
              {/* Row 2: status badge (mobile only) + metadata */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1 flex-wrap sm:flex-nowrap">
                <div className="sm:hidden">
                  {getStatusBadge(transaction.status)}
                </div>
                <span className="whitespace-nowrap">{format(new Date(transaction.created_at), 'MMM d, yyyy')}</span>
                {transaction.reference_number && (
                  <>
                    <span className="hidden sm:inline">•</span>
                    <span className="font-mono text-xs truncate" title={transaction.reference_number}>
                      {transaction.reference_number}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Desktop amount column — hidden on mobile (rendered inline above instead) */}
            <div className="text-right flex-shrink-0 hidden sm:block">
              <p className={`text-lg font-semibold ${
                isIncoming(transaction.type) ? 'text-green-600' : 'text-red-600'
              }`}>
                {isIncoming(transaction.type) ? '+' : '-'}${Math.abs(transaction.amount).toFixed(2)}
              </p>
              <p className="text-xs text-muted-foreground">{transaction.currency}</p>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};